import React, { useState, useCallback } from 'react';
import { message } from 'antd';
import ResponseCodeSection from '../../ApiTestSection/ResponseCodeSection';
import useRequestGenerator from '../hooks/useRequestGenerator';
import { formValuesToJson } from '../../../../utils/requestBodyUtils';

/**
 * EndpointResponseSection - Displays the API response and handles request execution
 * Shows a send button and displays response data
 *
 * @param {string} method - HTTP method (GET, POST, etc.)
 * @param {string} endpoint - API endpoint path
 * @param {Array} parameters - Array of API parameters
 * @param {Object} requestBody - Request body schema
 * @param {Object} fullSpec - Full OpenAPI specification
 * @param {string} serverUrl - Base server URL
 * @param {Object} formValues - Form values for parameters (optional)
 * @param {Function} onOpenModal - Function to open the API test modal (optional)
 */
const EndpointResponseSection = ({
  method,
  endpoint,
  parameters = [],
  requestBody = {},
  fullSpec,
  serverUrl = '',
  formValues = {},
  onOpenModal,
}) => {
  // When onOpenModal is provided (used in EndPointSection right column),
  // we just show the button to open the modal, no actual request handling
  if (onOpenModal) {
    return (
      <ResponseCodeSection
        response={null}
        loading={false}
        error={null}
        onSendRequest={onOpenModal}
        isModalTrigger={true}
      />
    );
  }

  // Below code is for when this component is used elsewhere with actual request handling
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { resolvedBodySchema } = useRequestGenerator({
    method,
    endpoint,
    parameters,
    requestBody,
    fullSpec,
    serverUrl,
    formValues,
  });

  // Handle API request submission
  const handleSendRequest = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const queryParamsObj = parameters
        ?.filter((param) => param?.in === 'query')
        ?.reduce((params, param) => {
          const value = formValues[param?.name] || param?.example || '';
          if (value) {
            params.append(param?.name, value);
          }
          return params;
        }, new URLSearchParams());

      const pathParams = parameters
        ?.filter((param) => param?.in === 'path')
        ?.reduce((url, param) => {
          return url?.replace(`{${param?.name}}`, formValues[param?.name] || '');
        }, endpoint);

      const baseUrl = `${serverUrl}${pathParams}`;
      const queryString = queryParamsObj.toString();
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const headers = parameters
        ?.filter((param) => param?.in === 'header')
        ?.reduce((acc, param) => {
          acc[param?.name] = formValues[param?.name] || param?.example || '';
          return acc;
        }, {});

      if (Object?.keys(requestBody)?.length) {
        headers['Content-Type'] = 'application/json';
      }

      const fetchOptions = {
        method: method,
        headers,
      };

      if (Object?.keys(requestBody)?.length) {
        const bodyData = formValuesToJson(formValues, resolvedBodySchema, fullSpec);
        fetchOptions.body = JSON.stringify(bodyData);
      }

      const apiResponse = await fetch(url, fetchOptions);

      // Check content type before parsing as JSON
      const contentType = apiResponse.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await apiResponse.json();
      } else {
        // Handle non-JSON responses
        const text = await apiResponse.text();
        data = {
          message: 'Non-JSON response received',
          contentType: contentType,
          body: text,
        };
      }

      setResponse({
        status: apiResponse.status,
        statusText: apiResponse.statusText,
        headers: Object.fromEntries(apiResponse.headers.entries()),
        data,
      });

      if (apiResponse.ok) {
        message.success('Request sent successfully');
      } else {
        message.warning(`Request completed with status ${apiResponse.status}`);
      }
    } catch (err) {
      console.error('API Request failed:', err);
      setError(err.message || 'Request failed');
      message.error('Request failed');
    } finally {
      setLoading(false);
    }
  }, [method, endpoint, parameters, requestBody, serverUrl, formValues, resolvedBodySchema, fullSpec]);

  return <ResponseCodeSection response={response} loading={loading} error={error} onSendRequest={handleSendRequest} />;
};

export default EndpointResponseSection;
